// src/pages/AiInsightPage.jsx
// Main AiInsight NLQ page
// - Left: chat history with user/AI bubbles
// - Right: Live Result Panel (Data / Insights / Outcomes sections) Ã¢â‚¬â€ opens on first query
// - Bottom: collapsible Report Builder with pinned items

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import ReactDOM from 'react-dom/client';
import {
    Alert, Badge, Box, Button, Chip, Collapse, Dialog, DialogContent,
    DialogTitle, Divider, InputAdornment, Skeleton,
    Snackbar, TextField, Typography, IconButton, Tooltip, GlobalStyles, Tab, Tabs,
} from '@mui/material';
import { useTimezoneFormatter } from '../hooks/useTimezoneFormatter';
import { useNavigate } from 'react-router-dom';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import StorageIcon from '@mui/icons-material/Storage';
import InsightsIcon from '@mui/icons-material/Insights';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import PushPinIcon from '@mui/icons-material/PushPin';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PreviewIcon from '@mui/icons-material/Preview';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import PsychologyIcon from '@mui/icons-material/Psychology';
import MemoryIcon from '@mui/icons-material/Memory';
import SpeedIcon from '@mui/icons-material/Speed';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DatasetIcon from '@mui/icons-material/Dataset';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import FunctionsIcon from '@mui/icons-material/Functions';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import GridOnIcon from '@mui/icons-material/GridOn';
import WaterfallChartIcon from '@mui/icons-material/WaterfallChart';
import DateRangeIcon from '@mui/icons-material/DateRange';



// Helper for relative time like "2h ago"
import { formatRelativeTime } from '../utils/dateTime';

import { useAuth } from '../context/useAuth';
import {
    getSessions, createSession, deleteSession, getSessionHistory, submitQuery, getSuggestions, renameSession,
} from '../api/aiinsight';
import { apiFetch } from '../api';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { markdownToHtml, isRawMarkdown } from '../components/aiinsight/narrativeUtils';

import SessionSidebar from '../components/aiinsight/SessionSidebar';
import InsightCard from '../components/aiinsight/InsightCard';
import ResultPanel from '../components/aiinsight/ResultPanel';
import AiChartRenderer from '../components/aiinsight/AiChartRenderer';
import VisualErrorBoundary from '../components/aiinsight/VisualErrorBoundary';
import ChatInputBar from '../components/aiinsight/ChatInputBar';
import ReportPreviewDialog from '../components/aiinsight/ReportPreviewDialog';
import ReportBuilderCard from '../components/aiinsight/ReportBuilderCard'; // kept for ref, replaced by ReportBuilder
import ReportBuilder from '../components/aiinsight/ReportBuilder';
import ReportCanvas from '../components/aiinsight/ReportCanvas';

// Ã¢â€â‚¬Ã¢â€â‚¬ Section card definitions Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
const SECTIONS = [
    {
        key: 'data',
        label: 'Data',
        icon: <StorageIcon sx={{ fontSize: 22 }} />,
        color: '#06b6d4',
        bg: 'rgba(6,182,212,0.08)',
        border: 'rgba(6,182,212,0.25)',
        hoverBg: 'rgba(6,182,212,0.15)',
        desc: 'Charts & visualizations',
        // filter: AI messages that are visualizations
        filter: (msg) => msg.type === 'ai' && (msg.content?.intent === 'visualize' || msg.content?.intent === 'compare') && msg.content?.status !== 'error',
    },
    {
        key: 'insights',
        label: 'Insights',
        icon: <InsightsIcon sx={{ fontSize: 22 }} />,
        color: '#a78bfa',
        bg: 'rgba(167,139,250,0.08)',
        border: 'rgba(167,139,250,0.25)',
        hoverBg: 'rgba(167,139,250,0.15)',
        desc: 'AI narratives & summaries',
        // filter: all AI responses that have a summary narrative (every successful query generates one)
        filter: (msg) => msg.type === 'ai' && msg.content?.summary_text && msg.content?.status !== 'error',
    },
    {
        key: 'outcomes',
        label: 'Outcomes',
        icon: <EmojiObjectsIcon sx={{ fontSize: 22 }} />,
        color: '#f59e0b',
        bg: 'rgba(245,158,11,0.08)',
        border: 'rgba(245,158,11,0.25)',
        hoverBg: 'rgba(245,158,11,0.15)',
        desc: 'All results combined',
        // filter: all successful AI messages
        filter: (msg) => msg.type === 'ai' && msg.content?.status !== 'error',
    },
];

// Ã¢â€â‚¬Ã¢â€â‚¬ Section Card Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
const SectionCard = ({ section, count, onClick, isActive }) => (
    <Box
        onClick={() => onClick(section.key)}
        sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: isActive ? section.color : section.border,
            bgcolor: isActive ? section.bg : 'background.paper',
            cursor: 'pointer',
            transition: 'all 0.18s ease',
            boxShadow: isActive ? `0 0 0 2px ${section.color}33` : 'none',
            '&:hover': {
                bgcolor: section.hoverBg,
                borderColor: section.color,
                boxShadow: `0 2px 16px ${section.color}22`,
                transform: 'translateY(-1px)',
            },
        }}
    >
        {/* Left accent bar */}
        <Box
            sx={{
                width: 3,
                height: 36,
                borderRadius: 4,
                bgcolor: section.color,
                flexShrink: 0,
            }}
        />

        {/* Icon */}
        <Box sx={{ color: section.color, flexShrink: 0, opacity: 0.85 }}>
            {section.icon}
        </Box>

        {/* Text */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: 13, lineHeight: 1.2 }}>
                {section.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, display: 'block' }}>
                {section.desc}
            </Typography>
        </Box>

        {/* Count badge */}
        <Box
            sx={{
                minWidth: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: isActive ? section.color : section.bg,
                border: `1px solid ${section.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    fontSize: 12,
                    color: isActive ? '#fff' : section.color,
                    lineHeight: 1,
                }}
            >
                {count}
            </Typography>
        </Box>
    </Box>
);

// Ã¢â€â‚¬Ã¢â€â‚¬ User message bubble Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
const UserBubble = ({ text, onEdit, timestamp }) => {
    const cleanText = text?.replace(/\[Format instructions:.*?\]/g, '')?.trim();
    const timeStr = timestamp ? formatDistanceToNow(new Date(timestamp), { addSuffix: true }) : 'just now';

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Box sx={{ maxWidth: '70%', position: 'relative' }} className="user-bubble-container">
                <Box
                    sx={{
                        bgcolor: '#007AFF',
                        borderRadius: '20px 20px 4px 20px',
                        px: 2.5,
                        py: 1.25,
                        boxShadow: '0 1px 0.5px rgba(0,0,0,0.1)',
                        position: 'relative',
                    }}
                >
                    <Typography variant="body2" sx={{ color: '#fff', lineHeight: 1.4, fontSize: '14px' }}>
                        {cleanText}
                    </Typography>
                </Box>
                {/* Edit icon and time - only show on hover, positioned on right */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.5,
                    justifyContent: 'flex-end',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '.user-bubble-container:hover &': { opacity: 1 },
                }}>
                    <Tooltip title="Edit message">
                        <IconButton
                            size="small"
                            onClick={() => onEdit(cleanText)}
                            sx={{
                                p: 0.3,
                                color: 'text.disabled',
                                opacity: 0.7,
                                '&:hover': { color: 'primary.main', opacity: 1 },
                                '& .MuiSvgIcon-root': { fontSize: 14 }
                            }}
                        >
                            <EditOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                        {timeStr}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// â”€â”€ AI thinking interactive loader (Minimal Professional) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const AIThinkingLoader = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [tipIndex, setTipIndex] = useState(0);

    const steps = [
        { label: 'Analyzing', subtext: 'your question' },
        { label: 'Processing', subtext: 'data model' },
        { label: 'Computing', subtext: 'insights' },
        { label: 'Generating', subtext: 'response' },
    ];

    const tips = [
        'Try "Compare" for side-by-side metrics',
        'Ask "Show trends" for time-based analysis',
        'Use "Top 10" for quick rankings',
        'Query across 50+ dimensions',
    ];

    useEffect(() => {
        const stepInterval = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % steps.length);
        }, 2000);
        return () => clearInterval(stepInterval);
    }, []);

    useEffect(() => {
        const tipInterval = setInterval(() => {
            setTipIndex((prev) => (prev + 1) % tips.length);
        }, 5000);
        return () => clearInterval(tipInterval);
    }, []);

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box sx={{ maxWidth: '72%', width: '100%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, opacity: 0.7 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 11, color: '#6366f1' }} />
                    <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 600, color: '#6366f1', letterSpacing: 0.5 }}>
                        AGENT INSIGHTS
                    </Typography>
                </Box>

                {/* Main Content Area */}
                <Box
                    sx={{
                        p: 1,
                        pl: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    {/* Interactive Pulse Animation */}
                    <Box sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        cursor: 'pointer',
                        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        '&:hover': {
                            transform: 'scale(1.15) rotate(90deg)',
                            '& .pulse-core': { boxShadow: '0 0 12px 4px rgba(99,102,241,0.4)', right: 0 }
                        }
                    }}>
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                bgcolor: '#6366f1',
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': {
                                    '0%, 100%': { transform: 'scale(0.6)', opacity: 0.3 },
                                    '50%': { transform: 'scale(1)', opacity: 0.1 },
                                },
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                borderRadius: '50%',
                                border: '2px solid #6366f1',
                                animation: 'pulse2 2s ease-in-out infinite 0.5s',
                                '@keyframes pulse2': {
                                    '0%, 100%': { transform: 'scale(0.8)', opacity: 0.5 },
                                    '50%': { transform: 'scale(1.1)', opacity: 0.2 },
                                },
                            }}
                        />
                        <Box
                            className="pulse-core"
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#6366f1',
                                transition: 'box-shadow 0.3s ease',
                            }}
                        />
                    </Box>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1', fontSize: 13 }}>
                                {steps[currentStep].label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
                                {steps[currentStep].subtext}
                            </Typography>
                        </Box>

                        {/* Progress Dots */}
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                            {steps.map((_, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        width: idx === currentStep ? 16 : 4,
                                        height: 4,
                                        borderRadius: 2,
                                        bgcolor: idx === currentStep ? '#6366f1' : 'rgba(99,102,241,0.3)',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Rotating Tip */}
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: 11,
                                display: 'block',
                                opacity: 0.8,
                            }}
                        >
                            {tips[tipIndex]}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// â”€â”€ AI thinking skeleton (deprecated, use AIThinkingLoader) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ThinkingSkeleton = () => <AIThinkingLoader />;

// -- Clarification Panel ------------------------------------------------------
// Shown when the AI detects an ambiguous metric. Shows friendly cards (no SQL)
// and a free-text "type your own" field so layman users can clarify naturally.
// Once submitted, the panel locks and highlights the chosen option.
const ClarificationPanel = ({ options = [], originalPrompt, onSelect, onCustom }) => {
    const [customText, setCustomText] = React.useState('');
    const [selectedIdx, setSelectedIdx] = React.useState(null);   // card pick
    const [customChosen, setCustomChosen] = React.useState('');   // custom text pick
    const submitted = selectedIdx !== null || !!customChosen;

    const handleCardClick = (opt, i) => {
        if (submitted) return;
        setSelectedIdx(i);
        onSelect(opt.formula);
    };
    const handleCustomSubmit = () => {
        if (submitted) return;
        const t = customText.trim();
        if (!t) return;
        setCustomChosen(t);
        onCustom(`${originalPrompt} -- specifically "${t}"`);
    };

    return (
        <Box sx={{
            mt: 0.5, mb: 1,
            background: (th) => th.palette.mode === 'dark' ? 'rgba(245,158,11,0.04)' : 'rgba(245,158,11,0.03)',
            border: submitted ? '1px solid rgba(99,102,241,0.35)' : '1px solid rgba(245,158,11,0.25)',
            borderRadius: 2.5, p: 2,
            transition: 'border-color 0.3s',
            opacity: submitted ? 0.85 : 1,
        }}>
            {/* Header — changes icon+text after submission */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: submitted ? 'rgba(99,102,241,0.12)' : 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.3s' }}>
                    <Typography sx={{ fontSize: 14, lineHeight: 1 }}>{submitted ? '✓' : '&#127919;'}</Typography>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: submitted ? '#818cf8' : '#f59e0b', lineHeight: 1.2, transition: 'color 0.3s' }}>
                        {submitted ? 'Clarification noted — running query…' : 'I found a few possible matches'}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                        {submitted ? '' : 'Pick the one that best matches what you meant, or describe it below in your own words.'}
                    </Typography>
                </Box>
            </Box>

            {/* Option Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: customChosen ? 1 : 2 }}>
                {options.map((opt, i) => {
                    const isChosen = selectedIdx === i;
                    const isDimmed = submitted && !isChosen;
                    return (
                        <Box
                            key={i}
                            onClick={() => handleCardClick(opt, i)}
                            sx={{
                                display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5, borderRadius: 2,
                                border: '1px solid',
                                borderColor: isChosen ? '#6366f1' : 'rgba(99,102,241,0.2)',
                                bgcolor: isChosen
                                    ? 'rgba(99,102,241,0.12)'
                                    : (th) => th.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                cursor: submitted ? 'default' : 'pointer',
                                opacity: isDimmed ? 0.35 : 1,
                                transition: 'all 0.2s ease',
                                boxShadow: isChosen ? '0 0 0 2px rgba(99,102,241,0.25)' : 'none',
                                ...(!submitted && {
                                    '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.07)', transform: 'translateY(-1px)', boxShadow: '0 2px 12px rgba(99,102,241,0.12)' }
                                }),
                            }}
                        >
                            {/* Badge — turns checkmark on selection */}
                            <Box sx={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: '1.5px solid', borderColor: isChosen ? '#6366f1' : 'rgba(99,102,241,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isChosen ? '#6366f1' : 'transparent', mt: '1px', transition: 'all 0.2s' }}>
                                <Typography sx={{ fontSize: isChosen ? 12 : 10, fontWeight: 700, color: isChosen ? '#fff' : '#6366f1' }}>
                                    {isChosen ? '✓' : i + 1}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 12.5, fontWeight: isChosen ? 700 : 600, color: isChosen ? 'primary.main' : 'text.primary', lineHeight: 1.3 }}>
                                    {opt.label}
                                </Typography>
                                {opt.description && <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>{opt.description}</Typography>}
                            </Box>
                            {opt.confidence != null && (
                                <Chip label={`${Math.round((opt.confidence || 0) * 100)}% match`} size="small" sx={{ height: 18, fontSize: 10, flexShrink: 0, bgcolor: isChosen ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.1)', color: isChosen ? '#6366f1' : '#818cf8', border: isChosen ? '1px solid #6366f1' : '1px solid rgba(99,102,241,0.2)', '& .MuiChip-label': { px: 0.75 } }} />
                            )}
                        </Box>
                    );
                })}
            </Box>

            {/* Custom text section — only show if not locked by a card pick */}
            {!submitted || customChosen ? (
                <>
                    {!customChosen && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                                <Typography sx={{ fontSize: 10, color: 'text.disabled', fontWeight: 600, letterSpacing: 0.5 }}>OR DESCRIBE IT YOURSELF</Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth size="small"
                                    placeholder={`e.g. "the total charged to insurance" or "just the patient count"`}
                                    value={customText}
                                    disabled={submitted}
                                    onChange={(e) => setCustomText(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCustomSubmit(); } }}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 12, borderRadius: 2, '&:hover fieldset': { borderColor: 'rgba(99,102,241,0.4)' }, '&.Mui-focused fieldset': { borderColor: '#6366f1' } } }}
                                />
                                <Tooltip title="Clarify with your own words">
                                    <span>
                                        <IconButton size="small" disabled={submitted || !customText.trim()} onClick={handleCustomSubmit} sx={{ bgcolor: customText.trim() && !submitted ? '#6366f1' : 'rgba(99,102,241,0.1)', color: customText.trim() && !submitted ? '#fff' : 'text.disabled', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: customText.trim() && !submitted ? '#4f46e5' : undefined }, transition: 'all 0.15s' }}>
                                            <ChevronRightIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </>
                    )}
                    {/* Show custom chosen text as a highlighted read-only chip */}
                    {customChosen && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, p: 1.25, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.25)' }}>
                            <Typography sx={{ fontSize: 11, color: '#818cf8', fontWeight: 600 }}>Your description:</Typography>
                            <Typography sx={{ fontSize: 12, color: 'primary.main', fontWeight: 600, fontStyle: 'italic' }}>"{customChosen}"</Typography>
                        </Box>
                    )}
                </>
            ) : null}
        </Box>
    );
};

// Ã¢â€â‚¬Ã¢â€â‚¬ Empty state Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
// ── Animated welcome / empty state constants ────────────────────────────────
const WELCOME_CAPABILITIES = [
    {
        icon: <QueryStatsIcon sx={{ fontSize: 22 }} />,
        color: '#6366f1', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.2)',
        title: 'Natural Language SQL',
        desc: 'Ask in plain English — the AI writes perfect SQL for you instantly.',
    },
    {
        icon: <BarChartIcon sx={{ fontSize: 22 }} />,
        color: '#06b6d4', bg: 'rgba(6,182,212,0.08)', border: 'rgba(6,182,212,0.2)',
        title: 'Auto Visualizations',
        desc: 'Smart chart selection — line, bar, donut, heatmap and 20+ more.',
    },
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 22 }} />,
        color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.2)',
        title: 'AI Narratives',
        desc: 'Every result comes with a human-readable insight and key takeaways.',
    },
    {
        icon: <DescriptionIcon sx={{ fontSize: 22 }} />,
        color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)',
        title: 'Report Builder',
        desc: 'Pin results, arrange them, and export a polished PDF report.',
    },
];

// ── Empty / Welcome Screen (Watermark) ──────────────────────────────────────
const EmptyChat = ({ sessionTitle }) => {
    const [visible, setVisible] = React.useState(false);
    React.useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                py: 4,
                gap: 4,
                opacity: visible ? 0.85 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(14px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
                maxWidth: 700,
                mx: 'auto',
                width: '100%',
                pointerEvents: 'none', // completely non-interactive watermark
                userSelect: 'none',
            }}
        >
            {/* ── Animated hero orb (Subtle) ── */}
            <Box sx={{ position: 'relative', width: 96, height: 96, flexShrink: 0, opacity: 0.9 }}>
                {/* Outer slow-spin dashed ring */}
                <Box sx={{
                    position: 'absolute',
                    inset: -16,
                    borderRadius: '50%',
                    border: '1.5px dashed rgba(99,102,241,0.2)',
                    animation: 'ai-spin 20s linear infinite',
                    '@keyframes ai-spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                }} />
                {/* Orbiting accent dot */}
                <Box sx={{
                    position: 'absolute',
                    width: 7, height: 7, borderRadius: '50%',
                    bgcolor: 'rgba(99,102,241,0.6)',
                    top: '50%', left: '50%',
                    animation: 'ai-orbit 20s linear infinite',
                    '@keyframes ai-orbit': {
                        from: { transform: 'translate(-50%,-50%) rotate(0deg) translateX(60px)' },
                        to:   { transform: 'translate(-50%,-50%) rotate(360deg) translateX(60px)' },
                    },
                }} />
                {/* Pulse ring */}
                <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    border: '1.5px solid rgba(99,102,241,0.2)',
                    animation: 'ai-pulse-ring 4s ease-in-out infinite',
                    '@keyframes ai-pulse-ring': {
                        '0%,100%': { transform: 'scale(1)', opacity: 0.5 },
                        '50%': { transform: 'scale(1.15)', opacity: 0.15 },
                    },
                }} />
                {/* Core muted circle */}
                <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.4) 0%, rgba(167,139,250,0.4) 55%, rgba(6,182,212,0.4) 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid rgba(99,102,241,0.3)',
                }}>
                    <AutoAwesomeIcon sx={{ fontSize: 36, color: 'text.secondary', opacity: 1 }} />
                </Box>
            </Box>

            {/* ── Headline (Subtle) ── */}
            <Box sx={{ textAlign: 'center' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        background: (t) => t.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#c4b5fd,#818cf8,#38bdf8)'
                            : 'linear-gradient(135deg,#6366f1,#8b5cf6,#06b6d4)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        mb: 0.75,
                        letterSpacing: '-0.3px',
                        lineHeight: 1.3,
                        opacity: 0.85,
                    }}
                >
                    {sessionTitle ? sessionTitle : 'Your AI Data Analyst'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto', lineHeight: 1.75 }}>
                    Ask anything about your data in plain English — I&apos;ll write the SQL,
                    run it, and craft beautiful charts and narratives instantly.
                </Typography>
            </Box>

            {/* ── Capabilities grid (Muted) ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, width: '100%', opacity: 0.85 }}>
                {WELCOME_CAPABILITIES.map((cap, i) => (
                    <Box
                        key={cap.title}
                        sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1.5,
                            p: 2, borderRadius: 2.5,
                            border: '1px dashed', borderColor: 'divider',
                            bgcolor: 'transparent',
                            opacity: visible ? 1 : 0,
                            transform: visible ? 'translateY(0)' : 'translateY(8px)',
                            transition: `opacity 0.6s ease ${0.1 + i * 0.07}s, transform 0.6s ease ${0.1 + i * 0.07}s`,
                        }}
                    >
                        <Box sx={{ color: 'text.secondary', flexShrink: 0, mt: 0.15 }}>{React.cloneElement(cap.icon, { sx: { fontSize: 20 } })}</Box>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 12.5, color: 'text.primary', lineHeight: 1.3 }}>
                                {cap.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11, lineHeight: 1.55, display: 'block', mt: 0.25 }}>
                                {cap.desc}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
// Main Page
// Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
const AiInsightPage = () => {
    const { token } = useAuth();
    const { formatDate } = useTimezoneFormatter();
    const navigate = useNavigate();

    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(true);
    const [activeSession, setActiveSession] = useState(null);

    const [dataModels, setDataModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(true);
    const [selectedModelId, setSelectedModelId] = useState('');

    const [messages, setMessages] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [queryLoading, setQueryLoading] = useState(false);
    // inputText lives INSIDE ChatInputBar â€” not here, to avoid re-rendering the whole page on every keystroke
    const chatInputRef = useRef(null);
    // Guard: track whether the "auto-select last session" on mount has already run
    const autoSelectDoneRef = useRef(false);

    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Live result panel state
    const [currentResult, setCurrentResult] = useState(null);
    const [resultPanelOpen, setResultPanelOpen] = useState(false);
    const [pinnedSections, setPinnedSections] = useState({});

    // Report Builder state
    const [reportItems, setReportItems] = useState([]);
    const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
    const [reportTitle, setReportTitle] = useState('My AI Report');

    // Canvas editor (Generate Report) state
    const [reportEditorOpen, setReportEditorOpen] = useState(false);
    const [reportEditorItems, setReportEditorItems] = useState([]);

    // Report Builder horizontal collapse â€” controlled by the single shared divider
    const REPORT_BUILDER_DEFAULT_WIDTH = 420;
    const REPORT_BUILDER_MIN_WIDTH = 350;
    const REPORT_BUILDER_COLLAPSE_THRESHOLD = 120;
    const [reportBuilderWidth, setReportBuilderWidth] = useState(0); // initially collapsed
    const reportBuilderWidthRef = useRef(0);
    const isDraggingReportBuilder = useRef(false);

    // Is the user allowed to manually collapse the Report Builder?
    // Once they receive an AI response (messages.length > 0), they cannot completely close it.
    const hasAIResponse = messages.some(m => m.type === 'ai');

    const autoOpenReportBuilder = () => {
        if (reportBuilderWidthRef.current === 0) {
            reportBuilderWidthRef.current = REPORT_BUILDER_DEFAULT_WIDTH;
            setReportBuilderWidth(REPORT_BUILDER_DEFAULT_WIDTH);
        }
    };

    // Single divider handler: left-right drag resizes the Report Builder (right = collapse)
    const onDividerMouseDown = (e) => {
        e.preventDefault();
        // If chat is empty, prevent dragging
        if (!hasAIResponse) return;
        isDraggingReportBuilder.current = true;
        const startX = e.clientX;
        const startW = reportBuilderWidthRef.current;
        const onMove = (ev) => {
            if (!isDraggingReportBuilder.current) return;
            // Drag left = wider report builder, drag right = narrower / collapse
            const delta = startX - ev.clientX;
            const newW = Math.max(0, Math.min(900, startW + delta));
            // Only allow snapping to 0 if NO AI messages have arrived
            const snapped = (!hasAIResponse && newW < REPORT_BUILDER_COLLAPSE_THRESHOLD)
                ? 0
                : Math.max(REPORT_BUILDER_MIN_WIDTH, newW);
            reportBuilderWidthRef.current = snapped;
            setReportBuilderWidth(snapped);
        };
        const onUp = () => {
            isDraggingReportBuilder.current = false;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const toggleReportBuilder = () => {
        // If has AI response, it cannot be collapsed
        if (hasAIResponse) return;
        const next = reportBuilderWidth === 0 ? REPORT_BUILDER_DEFAULT_WIDTH : 0;
        reportBuilderWidthRef.current = next;
        setReportBuilderWidth(next);
    };



    // Load and Persist Report Builder Drafts to LocalStorage
    useEffect(() => {
        if (!activeSession?.id) {
            setReportItems([]);
            return;
        }
        try {
            const saved = localStorage.getItem(`ai_insight_report_${activeSession.id}`);
            if (saved) setReportItems(JSON.parse(saved));
            else setReportItems([]);
        } catch {
            setReportItems([]);
        }
    }, [activeSession?.id]);

    useEffect(() => {
        if (!activeSession?.id) return;
        localStorage.setItem(`ai_insight_report_${activeSession.id}`, JSON.stringify(reportItems));
    }, [reportItems, activeSession?.id]);

    useEffect(() => {
        if (activeSession?.title && activeSession.title !== 'New Session') {
            setReportTitle(activeSession.title);
        }
    }, [activeSession?.title]);

    const [badgePulse, setBadgePulse] = useState(false);
    const nudgeShownRef = useRef(false);
    const sessionQueryCountRef = useRef(0);
    const reportRef = useRef(null);
    const [isPDFDownloading, setIsPDFDownloading] = useState(false);

    // Suggestions state
    const [suggestions, setSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true); // kept for compat but unused
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historySearch, setHistorySearch] = useState('');
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    
    // Live preview proxy state
    const [previewItems, setPreviewItems] = useState(null);
    const [previewTitle, setPreviewTitle] = useState(null);

    // Format options for insight generation
    const [formatOptions, setFormatOptions] = useState({
        bullets: true, summary: true, compact: true, dataMetrics: true, recommendations: false,
    });

    // AI model selection — persisted to localStorage
    const [aiModel, setAiModel] = useState(
        () => localStorage.getItem('aiinsight_ai_model') || 'default'
    );
    const handleAiModelChange = (modelId) => {
        setAiModel(modelId);
        localStorage.setItem('aiinsight_ai_model', modelId);
    };

    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);

    // Header renaming state
    const [editingHeader, setEditingHeader] = useState(false);
    const [headerTitle, setHeaderTitle] = useState('');
    const headerInputRef = useRef(null);

    const chatEndRef = useRef(null);

    // Scroll to bottom on new message
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, queryLoading]);

    // Fetch AI-generated suggestions whenever the model changes
    const fetchSuggestions = useCallback(async (modelId, lastQuestion = null) => {
        if (!modelId) {
            setSuggestions([]);
            return;
        }
        setSuggestionsLoading(true);
        try {
            const data = await getSuggestions(token, modelId, lastQuestion);
            setSuggestions(data.suggestions || []);
        } catch {
            // silently ignore Ã¢â‚¬â€ suggestions are non-critical
        } finally {
            setSuggestionsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (selectedModelId) fetchSuggestions(selectedModelId);
        else setSuggestions([]);
    }, [selectedModelId, fetchSuggestions]);

    // Load sessions on mount
    useEffect(() => {
        console.log('[AIInsight] Loading sessions...');
        setLoadingSessions(true);
        getSessions(token)
            .then((data) => {
                console.log('[AIInsight] Sessions loaded:', data);
                setSessions(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                console.error('[AIInsight] Failed to load sessions:', e);
                setError(e.message);
            })
            .finally(() => setLoadingSessions(false));
    }, [token]);

    // Initialize the default data model on load (prefer model from last session)
    useEffect(() => {
        if (autoSelectDoneRef.current || loadingSessions || activeSession) return;
        autoSelectDoneRef.current = true;
        
        if (sessions.length > 0) {
            const lastId = localStorage.getItem('aiinsight_last_session_id');
            const target = (lastId && sessions.find(s => s.id === lastId)) || sessions[0];
            if (target) {
                const modelId = target.model_id || target.connection_id || target.data_model_id;
                if (modelId) {
                    setSelectedModelId(modelId);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions, loadingSessions]);

    // Fetch data models once on mount
    useEffect(() => {
        setLoadingModels(true);
        apiFetch('/datamodels', token)
            .then((data) => setDataModels(Array.isArray(data) ? data : data?.items || []))
            .catch(() => setDataModels([]))
            .finally(() => setLoadingModels(false));
    }, [token]);

    // Pin or Unpin a section moving it to/from the Report Builder
    const handlePin = useCallback((sectionKey, item) => {
        setPinnedSections(prev => {
            if (prev[sectionKey]) {
                // Already pinned -> Unpin
                setReportItems(items => items.filter(p => p.id !== item.id));
                const next = { ...prev };
                delete next[sectionKey];
                return next;
            } else {
                // Not pinned -> Pin
                setReportItems(items => {
                    if (items.find(p => p.id === item.id)) return items;
                    return [...items, item];
                });
                setBadgePulse(true);
                setTimeout(() => setBadgePulse(false), 1800);
                return { ...prev, [sectionKey]: true };
            }
        });
    }, []);

    // Rename a report item title
    const handleRenameReportTitle = useCallback((idx, newTitle) => {
        setReportItems(prev => prev.map((item, i) => i === idx ? { ...item, title: newTitle } : item));
    }, []);

    // â”€â”€ Drag-reorder: mousedown on grip â†’ getBoundingClientRect detection â†’ mouseup commits â”€â”€
    // Most reliable approach: no HTML5 drag API, no elementFromPoint issues.
    // Cards are found by querying [data-report-card-idx] and checking physical screen rects.
    const [dragItemIdx, setDragItemIdx] = React.useState(null);
    const [dragOverIdx, setDragOverIdx] = React.useState(null);
    const dragSrcRef = React.useRef(null);
    const dragOverIdxRef = React.useRef(null);

    const handleDragStart = React.useCallback((srcIdx) => {
        dragSrcRef.current = srcIdx;
        dragOverIdxRef.current = srcIdx;
        setDragItemIdx(srcIdx);
        setDragOverIdx(srcIdx);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';

        const onMouseMove = (e) => {
            // Query every card element and find which one the cursor is inside
            const cards = document.querySelectorAll('[data-report-card-idx]');
            let found = null;
            let lowestCard = { bottom: -Infinity, idx: srcIdx };

            cards.forEach((el) => {
                const ci = parseInt(el.getAttribute('data-report-card-idx'), 10);
                const r = el.getBoundingClientRect();
                // Track the lowest card on screen (for "drag below all cards â†’ append")
                if (r.bottom > lowestCard.bottom) lowestCard = { bottom: r.bottom, idx: ci };
                if (e.clientX >= r.left && e.clientX <= r.right &&
                    e.clientY >= r.top && e.clientY <= r.bottom) {
                    found = ci;
                }
            });

            // If cursor is below all cards â†’ target the last card slot
            const target = found !== null
                ? found
                : (e.clientY > lowestCard.bottom ? lowestCard.idx : dragOverIdxRef.current);

            if (target !== dragOverIdxRef.current) {
                dragOverIdxRef.current = target;
                setDragOverIdx(target);
            }
        };

        const onMouseUp = () => {
            const from = dragSrcRef.current;
            const to = dragOverIdxRef.current;
            if (from !== null && to !== null && from !== to) {
                setReportItems(prev => {
                    const next = [...prev];
                    const [moved] = next.splice(from, 1);
                    next.splice(Math.min(to, next.length), 0, moved);
                    return next;
                });
            }
            // Cleanup
            dragSrcRef.current = null;
            dragOverIdxRef.current = null;
            setDragItemIdx(null);
            setDragOverIdx(null);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    // Kept for JSX prop compatibility (no longer does anything)
    const handleDragEnter = React.useCallback(() => { }, []);
    const handleDragEnd = React.useCallback(() => { }, []);
    const commitDrop = React.useCallback(() => { }, []);

    // Load session history when active session changes
    const handleSelectSession = useCallback(async (session) => {
        console.log('[AIInsight] Selecting session:', session.id, 'for user:', session.user_id);
        setError(null);
        setActiveSession(session);
        setMessages([]);
        setCurrentResult(null);
        setResultPanelOpen(false);
        setLoadingHistory(true);
        sessionQueryCountRef.current = 0;
        // Persist choice so we restore this session on next page visit
        localStorage.setItem('aiinsight_last_session_id', session.id);
        // Restore the model ID so the chat input enables
        const modelId = session.model_id || session.connection_id || session.data_model_id;
        if (modelId) {
            setSelectedModelId(modelId);
            fetchSuggestions(modelId);
        }
        try {
            console.log('[AIInsight] Fetching session history for:', session.id);
            const data = await getSessionHistory(token, session.id);
            console.log('[AIInsight] Received session history:', data);
            const queries = data.queries || [];
            console.log('[AIInsight] Processing', queries.length, 'queries');
            const msgs = queries.flatMap((q, idx) => [
                { type: 'user', content: q.user_prompt, timestamp: q.created_at || q.timestamp },
                { type: 'ai', content: q, timestamp: q.created_at || q.timestamp },
            ]);
            setMessages(msgs);

            // Restore result panel from the last successful query
            const lastSuccess = [...queries].reverse().find(
                (q) => {
                    if (q.status !== 'success') return false;
                    const hasD = (Array.isArray(q.result_json) && q.result_json.length > 0) || (q.result_json?.data?.length > 0);
                    return !!(hasD || q.viz_config_json);
                }
            );
            if (lastSuccess) {
                setCurrentResult(lastSuccess);
                setResultPanelOpen(true);
            }
            // Auto open report builder if there are AI messages
            if (msgs.some(m => m.type === 'ai')) {
                autoOpenReportBuilder();
            } else {
                // If it's a completely empty session, make sure it's collapsed
                reportBuilderWidthRef.current = 0;
                setReportBuilderWidth(0);
            }
        } catch (e) {
            console.error('[AIInsight] Failed to load session history:', e);
            console.error('[AIInsight] Error details:', {
                sessionId: session.id,
                errorMessage: e.message,
                errorStack: e.stack
            });
            setError(e.message);
        } finally {
            setLoadingHistory(false);
        }
    }, [token, fetchSuggestions]);

    // Create new session (now called only when first question is sent)
    const handleCreateSession = async (body) => {
        try {
            const newSession = await createSession(token, body);
            setSessions((prev) => [newSession, ...prev]);
            setActiveSession(newSession);
            setMessages([]);
            setCurrentResult(null);
            setResultPanelOpen(false);
            setSelectedModelId(body.model_id);
            fetchSuggestions(body.model_id);
            // Clear any existing error
            setError(null);

            // Auto-open report builder for profiling sessions
            if (body.title?.toLowerCase().includes('profiling')) {
                setTimeout(() => {
                    if (newSession.id) {
                        handleZeroClickProfile(newSession.id, body.model_id, body.title || 'Data Profiling');
                    }
                }, 50);
            }
        } catch (e) {
            let userMsg = e.message;
            if (userMsg && userMsg.includes("AIInsight is not enabled")) {
                userMsg = "AIInsight is not enabled for your plan. Please contact admin or raise a ticket to upgrade.";
                setError(userMsg);
                setSnackbar({ open: true, message: userMsg, severity: 'error' });
            } else {
                setError(userMsg);
                setSnackbar({ open: true, message: `Failed to create session: ${userMsg}`, severity: 'error' });
            }
        }
    };

    // Start new chat directly without popup
    const handleNewChat = () => {
        console.log('[AIInsight] Starting new chat');
        setActiveSession(null);
        setMessages([]);
        setCurrentResult(null);
        setResultPanelOpen(false);
        setSelectedModelId('');
        setSuggestions([]);
        setHistoryOpen(false);
        setError(null);
        // Remove any stored session preference
        localStorage.removeItem('aiinsight_last_session_id');
    };

    // Delete session
    const handleDeleteSession = async (sessionId) => {
        try {
            await deleteSession(token, sessionId);
            setSessions((prev) => prev.filter((s) => s.id !== sessionId));
            if (activeSession?.id === sessionId) {
                setActiveSession(null);
                setMessages([]);
            }
            setSnackbar({ open: true, message: 'Session deleted', severity: 'info' });
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Failed to delete session', severity: 'error' });
        }
    };

    const handleRenameSession = async (sessionId, newTitle) => {
        try {
            await renameSession(token, sessionId, newTitle);
            setSessions((prev) =>
                prev.map((s) => (s.id === sessionId ? { ...s, title: newTitle } : s))
            );
            if (activeSession?.id === sessionId) {
                setActiveSession((prev) => ({ ...prev, title: newTitle }));
            }
        } catch (err) {
            console.error(err);
            setSnackbar({ open: true, message: 'Failed to rename session', severity: 'error' });
        }
    };

    // Suggestion click: populate input then immediately submit (auto-creates session if needed)
    const handleSuggestionClick = async (text) => {
        if (queryLoading || !selectedModelId || !text?.trim()) return;
        const prompt = text.trim();
        chatInputRef.current?.setValue(prompt);
        // Delegate to handleSend to ensure current session/format settings are used
        handleSend(prompt);
        chatInputRef.current?.clear();
    };

    // Ã¢â€â‚¬Ã¢â€â‚¬ Zero-click Background Profiling Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    const handleZeroClickProfile = async (sessionId, modelId, currentTitle) => {
        const profilePrompt = "Provide a high-level overview of this dataset (total rows, key metrics, top categories), and visualize one of the most interesting distributions.";
        setQueryLoading(true);
        setMessages([{ type: 'user', content: profilePrompt, timestamp: new Date().toISOString() }]);

        try {
            const result = await submitQuery(token, sessionId, profilePrompt);
            setMessages((prev) => [...prev, { type: 'ai', content: result, timestamp: new Date().toISOString() }]);

            if (result.status !== 'error') {
                setCurrentResult(result);
                // Reveal the workspace with the profile
                setResultPanelOpen(true);
                setPinnedSections({});
                setSidebarOpen(false);
                autoOpenReportBuilder(); // strongly suggest report builder open here too

                // Re-label newly created generic sessions
                if (currentTitle && currentTitle.startsWith('Chat with')) {
                    setActiveSession((s) => ({ ...s, title: 'Data Profiling' }));
                    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title: 'Data Profiling' } : s)));
                    // Fire and forget rename
                    renameSession(token, sessionId, 'Data Profiling').catch(e => console.error(e));
                }
            }
            fetchSuggestions(modelId, profilePrompt);
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                { type: 'ai', content: { status: 'error', error_message: e.message || 'Auto-profile error', user_prompt: profilePrompt }, timestamp: new Date().toISOString() },
            ]);
        } finally {
            setQueryLoading(false);
        }
    };

    // Submit NLQ query â€” receives text directly from ChatInputBar (onSend(text))
    const handleSend = async (text) => {
        if (!text?.trim() || !selectedModelId || queryLoading) return;

        const prompt = text.trim();
        console.log('[AIInsight] Sending query:', prompt, 'selectedModelId:', selectedModelId, 'activeSession:', activeSession?.id);
        // ChatInputBar already cleared itself before calling onSend
        setQueryLoading(true);

        setMessages((prev) => [...prev, { type: 'user', content: prompt, timestamp: new Date().toISOString() }]);

        try {
            let sessionId = activeSession?.id;
            let currentSession = activeSession;
            let newlyCreated = false;

            if (!sessionId) {
                console.log('[AIInsight] No active session, creating new one...');
                const mName = dataModels.find(m => m.id === selectedModelId)?.name || 'Data';
                const body = { title: `Chat with ${mName}`, model_id: selectedModelId };
                currentSession = await createSession(token, body);
                sessionId = currentSession.id;
                newlyCreated = true;
                console.log('[AIInsight] Created new session:', sessionId);
                setSessions((prev) => [currentSession, ...prev]);
                setActiveSession(currentSession);
                // Store the new session ID for auto-selection on next visit
                localStorage.setItem('aiinsight_last_session_id', sessionId);
            }

            // Format options are sent as a dedicated API field â€” never pollute user_prompt
            const queryOptions = { ...formatOptions, ai_model: aiModel };
            const result = await submitQuery(token, sessionId, prompt, queryOptions);

            // Handle SMRE clarification request from backend
            if (result.status === 'needs_clarification') {
                setMessages((prev) => [
                    ...prev,
                    {
                        type: 'ai',
                        content: result,
                        originalPrompt: prompt,  // store for re-submission
                    },
                ]);
                setQueryLoading(false);
                return;
            }

            setMessages((prev) => [...prev, { type: 'ai', content: result, timestamp: new Date().toISOString() }]);

            // Show result in live panel (even if error, to prevent stale chart display)
            setCurrentResult(result);
            setResultPanelOpen(true);
            setPinnedSections({});
            setSidebarOpen(false);
            if (result.status !== 'error') {
                autoOpenReportBuilder(); // strongly force report builder open
            }

            // Smart nudge
            sessionQueryCountRef.current += 1;
            if (sessionQueryCountRef.current === 2 && !nudgeShownRef.current) {
                nudgeShownRef.current = true;
                setSnackbar({
                    open: true,
                    message: 'Pin sections from the right panel to build your report!',
                    severity: 'info',
                });
            }

            fetchSuggestions(selectedModelId, prompt);

            if (newlyCreated || currentSession.title.startsWith('Chat with')) {
                const shortTitle = prompt.length > 100 ? prompt.slice(0, 100) : prompt;
                setActiveSession((s) => ({ ...s, title: shortTitle }));
                setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title: shortTitle } : s)));
                // Persist the auto-generated title to backend
                apiFetch(`/aiinsight/sessions/${sessionId}`, token, {
                    method: 'PATCH',
                    body: JSON.stringify({ title: shortTitle })
                }).catch(err => console.error('[AIInsight] Failed to auto-save session title:', err));
            }
        } catch (e) {
            let errorMsg = e.message || 'An error occurred';
            if (errorMsg && errorMsg.includes("AIInsight is not enabled")) {
                errorMsg = "AIInsight is not enabled for your plan. Please contact admin or raise a ticket to upgrade.";
            } else if (errorMsg && errorMsg.includes("Parquet") && errorMsg.includes("404")) {
                errorMsg = "Oops! Something weird happened loading the data file. Please try again after refreshing the data model, or contact support if the issue persists.";
            }

            if (!activeSession || !activeSession.id) {
                // If creating session failed, user won't see the chat canvas.
                // Clear the queued user message and show a global error toast.
                setMessages([]);
                setError(errorMsg);
                setSnackbar({ open: true, message: errorMsg, severity: 'error' });
            } else {
                // If session exists but query failed, append error message to chat.
                setMessages((prev) => [
                    ...prev,
                    { type: 'ai', content: { status: 'error', error_message: errorMsg, user_prompt: prompt }, timestamp: new Date().toISOString() },
                ]);
            }
        } finally {
            setQueryLoading(false);
        }
    };

    // â”€â”€ Clarification chip handler â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // User confirmed which metric interpretation to use â†’ re-submit with resolved_formula
    const handleClarificationSelect = async (originalPrompt, formula) => {
        if (!originalPrompt || !formula) return;
        setQueryLoading(true);
        try {
            const sessionId = activeSession?.id;
            if (!sessionId) return;
            // Re-submit with resolved_formula so Flow 0 skips disambiguation
            const resolvedOptions = { ...(formatOptions || {}), resolved_formula: formula };
            const result = await submitQuery(token, sessionId, originalPrompt, resolvedOptions);
            setMessages((prev) => [...prev, { type: 'ai', content: result, timestamp: new Date().toISOString() }]);
            if (result.status !== 'error') {
                setCurrentResult(result);
                setResultPanelOpen(true);
                setPinnedSections({});
                setSidebarOpen(false);
            }
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                { type: 'ai', content: { status: 'error', error_message: e.message || 'Clarification failed', user_prompt: originalPrompt }, timestamp: new Date().toISOString() },
            ]);
        } finally {
            setQueryLoading(false);
        }
    };

    // Ã¢â€â‚¬Ã¢â€â‚¬ Compute section counts from messages Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    const getSectionItems = (sectionKey) => {
        const section = SECTIONS.find(s => s.key === sectionKey);
        if (!section) return [];
        return messages
            .filter(section.filter)
            .map(msg => msg.content);
    };

    const sectionCounts = Object.fromEntries(
        SECTIONS.map(s => [s.key, messages.filter(s.filter).length])
    );

    const handleToggleSidebar = () => {
        setSidebarOpen((o) => !o);
    };

    const handleSectionClick = () => {
        // kept for compatibility
    };

    // Ã¢â€â‚¬Ã¢â€â‚¬ Report item operations Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬
    const handleRemoveReportItem = (idx) => {
        setReportItems((prev) => prev.filter((_, i) => i !== idx));
    };

    // CSV export for report builder
    const handleExportCSV = () => {
        if (reportItems.length === 0) return;
        const esc = v => { if (v == null) return ''; const s = String(v).replace(/"/g, '""'); return s.includes(',') || s.includes('\n') ? `"${s}"` : s; };
        const rows = [];
        reportItems.forEach((item, i) => {
            if (i > 0) rows.push(['']);
            rows.push([`"Question: ${(item.title || '').replace(/"/g, '""')}"`]);
            if (item.result_json?.length > 0) {
                const cols = Object.keys(item.result_json[0]);
                rows.push(cols.map(esc));
                item.result_json.forEach(row => rows.push(cols.map(c => esc(row[c]))));
            } else {
                rows.push(['"Summary"'], [`"${(item.narrative || '').replace(/<[^>]+>/g, '').replace(/"/g, '""')}"`]);
            }
        });
        const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${reportTitle}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    const handleEditReportTitle = (idx, newTitle) => {
        setReportItems((prev) => prev.map((item, i) => i === idx ? { ...item, title: newTitle } : item));
    };

    const handleEditReportNarrative = (idx, newNarrative) => {
        setReportItems((prev) => prev.map((item, i) => i === idx ? { ...item, narrative: newNarrative } : item));
    };

    // â”€â”€ Auto Format: re-order for optimal report layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    // Strategy: narrative/insight items first (full-width context setters),
    // then chart/data items (visual evidence), grouped together so the
    // preview can pair them side-by-side cleanly.
    const handleAutoFormat = () => {
        setReportItems((prev) => {
            if (prev.length <= 1) return prev;
            const narratives = prev.filter(it => !it.viz_config_json);
            const charts = prev.filter(it => !!it.viz_config_json);
            return [...narratives, ...charts];
        });
        setSnackbar({ open: true, severity: 'success', message: 'Auto-formatted: narratives first, charts grouped for side-by-side layout.' });
    };

    // PDF export Ã¢â‚¬â€ generates a full-page styled HTML report in a new window, then prints
    const handleDownloadPDF = async () => {
        if (reportItems.length === 0) return;
        setIsPDFDownloading(true);

        const sectionColor = { visualize: '#06b6d4', summarize: '#a78bfa', answer: '#f59e0b' };
        const sectionLabel = { visualize: 'DATA', summarize: 'INSIGHTS', answer: 'OUTCOMES' };

        const cleanText = (html) => (html || '').replace(/<[^>]+>/g, '');

        // Render chart offscreen Ã¢â€ â€™ capture via html2canvas Ã¢â€ â€™ return base64 PNG
        const renderChartToPng = async (item) => {
            const CHART_W = 700, CHART_H = 300;
            const wrapper = document.createElement('div');
            wrapper.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${CHART_W}px;height:${CHART_H}px;background:#ffffff;z-index:-1;`;
            document.body.appendChild(wrapper);
            let root = null;
            try {
                root = ReactDOM.createRoot(wrapper);
                root.render(<AiChartRenderer vizConfig={item.viz_config_json} data={item.result_json} />);
                // Wait for Recharts to fully render (needs layout + paint)
                await new Promise((r) => setTimeout(r, 1000));

                // Find the [data-ai-chart] div we added as a marker
                const chartDiv = wrapper.querySelector('[data-ai-chart="true"]');
                if (!chartDiv) return null;

                // Capture with html2canvas directly on the live DOM element Ã¢â‚¬â€
                // this grabs real rendered pixels, no SVG serialization needed
                const captureCanvas = await html2canvas(chartDiv, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    backgroundColor: '#ffffff',
                    width: CHART_W,
                    height: CHART_H,
                });
                return captureCanvas.toDataURL('image/png');
            } catch {
                return null;
            } finally {
                root?.unmount();
                document.body.removeChild(wrapper);
            }
        };

        // Ã¢â€â‚¬Ã¢â€â‚¬ Capture chart PNG for each visualize item Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬Ã¢â€â‚¬

        const chartImages = {};
        for (const item of reportItems) {
            if (item.viz_config_json && (item.intent === 'visualize' || item.intent === 'compare')) {
                chartImages[item.id] = await renderChartToPng(item);
            }
        }

        const itemsHtml = reportItems.map((item, idx) => {
            const color = sectionColor[item.intent] || '#a78bfa';
            const label = sectionLabel[item.intent] || 'INSIGHTS';
            // Convert markdown narrative to sanitised HTML for PDF iframe rendering
            const rawNarrative = item.narrative || '';
            const narrativeHtml = rawNarrative
                ? (isRawMarkdown(rawNarrative) ? markdownToHtml(rawNarrative) : rawNarrative)
                : '';
            const hasChart = item.viz_config_json && (item.intent === 'visualize' || item.intent === 'compare');
            const chartImg = chartImages[item.id];

            const chartHtml = hasChart && chartImg
                ? `<img src="${chartImg}" style="width:100%;border-radius:8px;margin-top:14px;display:block;" />`
                : '';

            // Show table only when there's no chart
            const tableHtml = !hasChart && item.result_json?.length > 0 ? (() => {
                const cols = Object.keys(item.result_json[0]);
                const formatValue = (value) => {
                    if (value === null || value === undefined || value === '') return value;
                    const num = parseFloat(value);
                    if (isNaN(num)) return value;
                    return num % 1 === 0 ? num : num.toFixed(2);
                };
                const headerRow = cols.map(c => `<th style="padding:6px 10px;text-align:left;border-bottom:2px solid ${color}33;font-size:11px;color:#555;font-weight:700">${c.replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase())}</th>`).join('');
                const dataRows = item.result_json.slice(0, 50).map(row =>
                    `<tr>${cols.map(c => `<td style="padding:5px 10px;border-bottom:1px solid #f0f0f0;font-size:11px">${formatValue(row[c]) ?? ''}</td>`).join('')}</tr>`
                ).join('');
                return `<table style="width:100%;border-collapse:collapse;margin-top:10px">
                    <thead><tr>${headerRow}</tr></thead>
                    <tbody>${dataRows}</tbody>
                </table>`;
            })() : '';

            return `
            <div style="border:1px solid ${color}33;border-radius:12px;padding:18px 20px;background:#fff;display:inline-block;width:100%;page-break-inside:avoid">
                <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px">
                    <span style="font-size:9px;font-weight:800;letter-spacing:1px;color:${color};background:${color}18;
                          padding:2px 8px;border-radius:20px;border:1px solid ${color}33">${label}</span>
                    <span style="font-size:11px;font-weight:700;color:#1a1a2e;flex:1">${item.title || 'Untitled'}</span>
                </div>
                ${narrativeHtml ? `<div style="font-size:12px;line-height:1.75;color:#333">${narrativeHtml}</div>` : ''}
                ${chartHtml}
                ${tableHtml}
            </div>`;
        }).join('');

        const html = [
            '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
            `<title>${reportTitle || 'AI Insight Report'}</title>`,
            '<style>',
            '*{box-sizing:border-box;margin:0;padding:0}',
            'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;background:#f8fafc;color:#1a1a2e;padding:28px}',
            '.hdr{border-bottom:3px solid #6366f1;padding-bottom:14px;margin-bottom:22px}',
            '.hdr h1{font-size:20px;font-weight:800}.hdr p{font-size:11px;color:#888;margin-top:4px}',
            '.grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}',
            '.card{border-radius:10px;background:#fff;padding:14px 16px;border:1px solid #e2e8f0;break-inside:avoid}',
            '.card-hdr{display:flex;align-items:center;gap:6px;margin-bottom:8px}',
            '.badge{font-size:9px;font-weight:800;letter-spacing:1px;padding:2px 7px;border-radius:20px}',
            '.ctitle{font-size:12px;font-weight:700;flex:1;line-height:1.3}',
            '.narr{font-size:11.5px;line-height:1.75;color:#333}',
            '.narr ul{padding-left:24px;margin:4px 0;list-style-type:disc;list-style-position:outside}',
            '.narr ol{padding-left:24px;margin:4px 0;list-style-type:decimal;list-style-position:outside}',
            '.narr li{margin-bottom:2px;display:list-item}',
            '.narr strong,.narr b{font-weight:700}.narr em,.narr i{font-style:italic}.narr u{text-decoration:underline}',
            'good{color:#16a34a;font-weight:600}bad{color:#dc2626;font-weight:600}',
            '.narr h2,.narr h3,.narr h4{color:#7c3aed;font-weight:700;margin:8px 0 4px 0;font-size:12px}',
            'table{width:100%;border-collapse:collapse;margin-top:8px;font-size:10.5px}',
            'th{padding:4px 7px;text-align:left;border-bottom:2px solid #e2e8f0;font-weight:700;color:#555;font-size:10px}',
            'td{padding:4px 7px;border-bottom:1px solid #f0f0f0}',
            'img{width:100%;border-radius:6px;margin-top:8px}',
            '.foot{margin-top:26px;padding-top:12px;border-top:1px solid #eee;font-size:10px;color:#aaa;text-align:center}',
            '@media print{body{background:#fff;padding:14px}@page{margin:12mm;size:A4}}',
            '</style></head><body>',
            '<div class="hdr">',
            `<div style="display:flex;align-items:center;gap:7px"><span style="font-size:17px">&#10024;</span><h1>${reportTitle || 'AI Insight Report'}</h1></div>`,
            `<p>Generated ${formatDate(new Date())} &middot; ${reportItems.length} item${reportItems.length !== 1 ? 's' : ''}</p>`,
            '</div>',
            `<div class="grid">${itemsHtml}</div>`,
            '<div class="foot">Generated by AI Insight Report Builder</div>',
            '</body></html>',
        ].join('\n');


        // Render HTML in a hidden off-screen iframe â†’ capture with html2canvas â†’ save as real PDF
        const IFRAME_W = 960;
        const iframe = document.createElement('iframe');
        iframe.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${IFRAME_W}px;height:1400px;border:0;visibility:hidden;`;
        document.body.appendChild(iframe);

        const iDoc = iframe.contentDocument || iframe.contentWindow.document;
        iDoc.open();
        iDoc.write(html);
        iDoc.close();

        // Wait for fonts, images, layout
        await new Promise((r) => setTimeout(r, 1200));

        try {
            const canvas = await html2canvas(iDoc.body, {
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#f8fafc',
                width: IFRAME_W,
                scrollY: 0,
                windowWidth: IFRAME_W,
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.88);
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const pageW = pdf.internal.pageSize.getWidth();   // 210mm
            const pageH = pdf.internal.pageSize.getHeight();  // 297mm
            const ratio = canvas.width / pageW;               // px per mm
            const totalH = canvas.height / ratio;              // total content height in mm
            const pages = Math.ceil(totalH / pageH);

            for (let i = 0; i < pages; i++) {
                if (i > 0) pdf.addPage();
                // Shift the image up by i*pageH mm on each page
                pdf.addImage(imgData, 'JPEG', 0, -i * pageH, pageW, totalH);
            }

            pdf.save(`${(reportTitle || 'AI-Insight-Report').replace(/\s+/g, '-')}.pdf`);
        } finally {
            document.body.removeChild(iframe);
            setIsPDFDownloading(false);
        }
    };



    const handleReorderReportItems = (fromIdx, toIdx) => {
        setReportItems((prev) => {
            const next = [...prev];
            const [moved] = next.splice(fromIdx, 1);
            next.splice(toIdx, 0, moved);
            return next;
        });
    };


    // ── Generate Report: open canvas editor with selected items ────────────
    const handleOpenReportEditor = useCallback((selectedIds) => {
        const selected = reportItems.filter(item => selectedIds.includes(item.id));
        if (selected.length === 0) return;
        setReportEditorItems(selected);
        setReportEditorOpen(true);
    }, [reportItems]);

    const handleCloseReportEditor = useCallback(() => {
        setReportEditorOpen(false);
        setReportEditorItems([]);
    }, []);

    // ── ReportBuilder item handlers ──────────────────────────────────────────



    const activeModelName = dataModels.find((m) => m.id === selectedModelId)?.name;

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: 'flex', height: '100%', minHeight: 0, bgcolor: 'background.default', overflow: 'hidden', position: 'relative' }}>

            {/* ── Global hover-only scrollbar styles ── */}
            <GlobalStyles styles={{
                '*::-webkit-scrollbar': { width: '5px', height: '5px' },
                '*::-webkit-scrollbar-track': { background: 'transparent' },
                '*::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px', transition: 'background 0.2s' },
                '*:hover::-webkit-scrollbar-thumb': { background: 'rgba(120,120,140,0.28)' },
                '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(120,120,140,0.52)' },
                '*': { scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent' },
                '*:hover': { scrollbarColor: 'rgba(120,120,140,0.28) transparent' },
            }} />

            {/* ── CHAT COLUMN ── */}
            <Box sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                position: 'relative',
                bgcolor: 'background.paper',
                overflow: 'hidden',
                transition: 'width 0.3s cubic-bezier(0.22,1,0.36,1)',
            }}>

                {/* Top bar */}
                <Box sx={{
                    px: 3, py: 1.25,
                    borderBottom: '1px solid', borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex', alignItems: 'flex-start', gap: 1.5, minHeight: 56, flexShrink: 0,
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AutoAwesomeIcon sx={{ color: '#6366f1', fontSize: 22, flexShrink: 0 }} />
                            {editingHeader && activeSession ? (
                                <input
                                    ref={headerInputRef}
                                    value={headerTitle}
                                    onChange={(e) => setHeaderTitle(e.target.value)}
                                    onBlur={() => {
                                        if (headerTitle.trim() && headerTitle !== activeSession.title)
                                            handleRenameSession(activeSession.id, headerTitle.trim());
                                        setEditingHeader(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            if (headerTitle.trim() && headerTitle !== activeSession.title)
                                                handleRenameSession(activeSession.id, headerTitle.trim());
                                            setEditingHeader(false);
                                        }
                                        if (e.key === 'Escape') setEditingHeader(false);
                                    }}
                                    style={{
                                        fontSize: '1.1rem', fontWeight: 700, fontFamily: 'inherit',
                                        border: 'none', outline: '2px solid #6366f1', borderRadius: 4,
                                        padding: '0 8px', background: 'transparent', color: 'inherit', width: '100%', maxWidth: 300,
                                    }}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, '&:hover .title-edit-btn': { opacity: 1 } }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700, color: 'text.primary', fontSize: '1.1rem',
                                            cursor: activeSession ? 'pointer' : 'default', lineHeight: 1.3,
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word',
                                        }}
                                        onDoubleClick={() => {
                                            if (activeSession) { setHeaderTitle(activeSession.title || ''); setEditingHeader(true); setTimeout(() => headerInputRef.current?.select(), 50); }
                                        }}
                                        title={activeSession ? 'Double-click to rename' : ''}
                                    >
                                        {activeSession ? activeSession.title || 'Unnamed Session' : 'Agent Insights'}
                                    </Typography>
                                    {activeSession && (
                                        <IconButton size="small"
                                            className="title-edit-btn"
                                            onClick={() => { setHeaderTitle(activeSession.title || ''); setEditingHeader(true); setTimeout(() => headerInputRef.current?.select(), 50); }}
                                            sx={{ opacity: 0, transition: 'opacity 0.2s', color: 'text.secondary', width: 26, height: 26, flexShrink: 0, '&:hover': { color: 'primary.main', bgcolor: 'rgba(99,102,241,0.08)' } }}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Top-bar right: icons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, alignSelf: 'center' }}>
                        {messages.length > 0 && (
                            <Tooltip title="Search chat history" placement="bottom">
                                <IconButton size="small" onClick={() => setShowSearch(prev => !prev)}
                                    sx={{ width: 32, height: 32, color: showSearch ? 'primary.main' : 'text.secondary', bgcolor: showSearch ? 'rgba(99,102,241,0.1)' : 'transparent', '&:hover': { color: 'primary.main', bgcolor: 'rgba(99,102,241,0.1)' } }}>
                                    <SearchIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="New chat" placement="bottom">
                            <IconButton size="small" onClick={handleNewChat}
                                sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(99,102,241,0.1)' } }}>
                                <AddIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Chat history" placement="bottom">
                            <IconButton size="small" onClick={() => { setHistoryOpen(o => !o); setHistorySearch(''); }}
                                sx={{ width: 32, height: 32, color: historyOpen ? 'primary.main' : 'text.secondary', bgcolor: historyOpen ? 'rgba(99,102,241,0.1)' : 'transparent', '&:hover': { color: 'primary.main', bgcolor: 'rgba(99,102,241,0.1)' } }}>
                                <HistoryIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="How Agent Insights works" placement="bottom">
                            <IconButton size="small" onClick={() => setInfoModalOpen(true)}
                                sx={{ width: 32, height: 32, color: 'text.secondary', '&:hover': { color: '#6366f1', bgcolor: 'rgba(99,102,241,0.1)' } }}>
                                <InfoOutlinedIcon sx={{ fontSize: 19 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* AI Insight Info Modal */}
                <AiInsightInfoModal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} />

                {/* Floating history popup */}
                {historyOpen && (
                    <>
                        <Box onClick={() => setHistoryOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 1200, bgcolor: 'transparent' }} />
                        <Box onClick={(e) => e.stopPropagation()} sx={{
                            position: 'absolute', top: 56, right: 0, width: 320, maxHeight: '70vh', zIndex: 1300,
                            display: 'flex', flexDirection: 'column', borderRadius: 3,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
                            bgcolor: (t) => t.palette.mode === 'dark' ? '#1e1e2e' : '#ffffff',
                            border: '1px solid', borderColor: 'divider', overflow: 'hidden',
                        }}>
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>Chat history</Typography>
                            </Box>
                            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                                <TextField size="small" fullWidth autoFocus placeholder="Search conversations..."
                                    value={historySearch} onChange={(e) => setHistorySearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></InputAdornment>,
                                        endAdornment: historySearch ? (<InputAdornment position="end"><IconButton size="small" onClick={() => setHistorySearch('')} sx={{ p: 0.25 }}><ClearIcon sx={{ fontSize: 13 }} /></IconButton></InputAdornment>) : null,
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc', '& fieldset': { borderColor: 'transparent' }, '&:hover fieldset': { borderColor: 'divider' }, '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 } } }}
                                />
                            </Box>
                            <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 } }}>
                                {loadingSessions ? (
                                    <Box sx={{ px: 2, pt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                        {[1, 2, 3].map(i => <Skeleton key={i} height={38} sx={{ borderRadius: 2 }} />)}
                                    </Box>
                                ) : (() => {
                                    const q = historySearch.toLowerCase();
                                    // Ensure naive datetime strings from the server are parsed as UTC,
                                    // not local time â€” otherwise IST users see a ~5.5 h offset.
                                    const parseUTC = (s) => {
                                        if (!s) return new Date();
                                        return new Date(s.endsWith('Z') || s.includes('+') ? s : s + 'Z');
                                    };
                                    const now = new Date();
                                    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
                                    const groups = {};
                                    (sessions || []).filter(s => !q || (s.title || '').toLowerCase().includes(q)).forEach(s => {
                                        const d = new Date(s.updated_at);
                                        const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                                        const label = day >= today ? 'Today' : day >= yesterday ? 'Yesterday' : 'Older';
                                        if (!groups[label]) groups[label] = [];
                                        groups[label].push(s);
                                    });
                                    const order = ['Today', 'Yesterday', 'Older'];
                                    const allEmpty = Object.values(groups).every(g => !g?.length);
                                    if (allEmpty) return (<Box sx={{ py: 4, textAlign: 'center' }}><Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 12.5 }}>{historySearch ? `No results for "${historySearch}"` : 'No chat history yet'}</Typography></Box>);
                                    return order.map(label => !groups[label]?.length ? null : (
                                        <Box key={label}>
                                            <Typography variant="caption" sx={{ display: 'block', px: 2, pt: 1.5, pb: 0.5, color: 'text.disabled', fontWeight: 700, fontSize: 10, letterSpacing: 0.8, textTransform: 'uppercase' }}>{label}</Typography>
                                            {groups[label].map(session => (
                                                <Box key={session.id} onClick={() => { handleSelectSession(session); setHistoryOpen(false); }}
                                                    sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, mx: 1, mb: 0.25, borderRadius: 2, cursor: 'pointer', bgcolor: activeSession?.id === session.id ? (t) => t.palette.mode === 'dark' ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.09)' : 'transparent', '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }, transition: 'background 0.12s', gap: 1.5 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: activeSession?.id === session.id ? 600 : 400, color: activeSession?.id === session.id ? 'primary.main' : 'text.primary' }}>{session.title || 'Unnamed Chat'}</Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11, flexShrink: 0 }}>
                                                        {formatDistanceToNow((() => { const s2 = session.updated_at; return new Date(s2 && (s2.endsWith('Z') || s2.includes('+')) ? s2 : (s2 || '') + 'Z'); })(), { addSuffix: true })}
                                                    </Typography>
                                                    <Tooltip title="Delete" placement="top">
                                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteSession(session.id); }}
                                                            sx={{ p: 0.4, color: 'text.disabled', opacity: 0, '.MuiBox-root:hover &': { opacity: 1 }, '&:hover': { color: 'error.main', opacity: 1 } }}>
                                                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Box>
                                    ));
                                })()}
                            </Box>
                            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                                <Box onClick={handleNewChat} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2, cursor: 'pointer', color: 'primary.main', '&:hover': { bgcolor: 'rgba(99,102,241,0.08)' }, transition: 'background 0.12s' }}>
                                    <AddIcon sx={{ fontSize: 18 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>New chat</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </>
                )}

                {/* Error banner */}
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 2, mt: 1, borderRadius: 2 }}
                        action={<Button color="inherit" size="small" onClick={() => activeSession && handleSelectSession(activeSession)}>RETRY</Button>}>
                        {error}
                    </Alert>
                )}

                {/* Search bar */}
                {activeSession && messages.length > 0 && (
                    <Collapse in={showSearch} unmountOnExit>
                        <Box sx={{ px: 3, py: 1.5, pb: 0.5, flexShrink: 0, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <TextField size="small" placeholder="Search chat history..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>, endAdornment: searchQuery ? (<InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment>) : null }}
                                sx={{ width: '100%', '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f8fafc' } }}
                            />
                        </Box>
                    </Collapse>
                )}

                {/* Chat messages area */}
                <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, display: 'flex', flexDirection: 'column', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'rgba(99,102,241,0.2)', borderRadius: 3 } }}>
                    {(!activeSession && messages.length === 0) ? (
                        (!loadingModels && dataModels.length === 0) ? (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                <StorageIcon sx={{ color: 'text.disabled', fontSize: 48 }} />
                                <Typography variant="h6" sx={{ color: 'text.primary', textAlign: 'center' }}>No Data Models Found</Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 400 }}>You need to create a data model before you can start asking questions.</Typography>
                                <Button variant="contained" onClick={() => navigate('/datamodels')} sx={{ mt: 2, borderRadius: 2, textTransform: 'none', px: 4 }}>Create Data Model</Button>
                            </Box>
                        ) : (
                            <EmptyChat onPromptClick={selectedModelId ? handleSuggestionClick : null} />
                        )
                    ) : loadingHistory ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={80} sx={{ bgcolor: 'divider', borderRadius: 3 }} />)}
                        </Box>
                    ) : messages.length === 0 ? (
                        <EmptyChat sessionTitle={activeSession?.title} onPromptClick={handleSuggestionClick} />
                    ) : (() => {
                        const sq = searchQuery.trim().toLowerCase();
                        let filtered = messages;
                        if (sq) {
                            const keepIndices = new Set();
                            messages.forEach((msg, i) => {
                                const userText = (msg.type === 'user' ? msg.content : '').toLowerCase();
                                const aiText = (msg.type === 'ai' ? (msg.content?.user_prompt || '') + ' ' + (msg.content?.summary_text || '') : '').toLowerCase();
                                if (userText.includes(sq) || aiText.includes(sq)) {
                                    keepIndices.add(i);
                                    if (msg.type === 'user' && messages[i + 1]?.type === 'ai') keepIndices.add(i + 1);
                                    if (msg.type === 'ai' && i > 0 && messages[i - 1]?.type === 'user') keepIndices.add(i - 1);
                                }
                            });
                            filtered = messages.filter((_, i) => keepIndices.has(i));
                        }
                        if (filtered.length === 0) return (
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, opacity: 0.5 }}>
                                <SearchIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>No results for "{searchQuery}"</Typography>
                            </Box>
                        );
                        return filtered.map((msg, idx) => {
                            const messageKey = msg.type === 'ai' && msg.content?.id ? msg.content.id : `msg-${idx}-${msg.timestamp || ''}`;
                            return msg.type === 'user' ? (
                                <UserBubble key={messageKey} text={msg.content} onEdit={(txt) => chatInputRef.current?.setValue(txt)} timestamp={msg.timestamp} />
                            ) : (
                                <Box key={messageKey} sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3, position: 'relative' }}>
                                    <Box sx={{ width: '100%' }}>
                                        {msg.content && msg.content.status === 'needs_clarification' ? (
                                            <ClarificationPanel
                                                options={msg.content?.result_json?.clarification_options || msg.content?.clarification_options || []}
                                                originalPrompt={msg.originalPrompt}
                                                onSelect={(formula) => handleClarificationSelect(msg.originalPrompt, formula)}
                                                onCustom={(customPrompt) => handleSuggestionClick(customPrompt)}
                                            />

                                        ) : msg.content && msg.content.status !== 'error' ? (
                                            <Box sx={{ position: 'relative', '&:hover .hover-actions': { opacity: 1 } }}>
                                                <VisualErrorBoundary showReset>
                                                    <ResultPanel result={msg.content} onClose={null} onPin={handlePin} pinnedSections={pinnedSections} onRerun={(prompt) => handleSuggestionClick(prompt)} readOnly={true} />
                                                </VisualErrorBoundary>
                                                <Box className="hover-actions" sx={{ position: 'absolute', bottom: -20, left: 8, opacity: 0, transition: 'opacity 0.2s', zIndex: 10 }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Copy response"><IconButton size="small" onClick={() => { navigator.clipboard.writeText(msg.content?.summary_text || JSON.stringify(msg.content?.result_json || '')); setSnackbar({ open: true, message: 'Copied!', severity: 'success' }); }} sx={{ fontSize: 10, color: 'text.secondary', '&:hover': { color: 'primary.main' }, p: 0.5 }}><ContentCopyIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                                                        <Tooltip title="Retry"><IconButton size="small" onClick={() => handleSuggestionClick(msg.content?.user_prompt)} sx={{ fontSize: 10, color: 'text.secondary', '&:hover': { color: 'primary.main' }, p: 0.5 }}><RefreshIcon sx={{ fontSize: 12 }} /></IconButton></Tooltip>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ position: 'relative', '&:hover .hover-actions': { opacity: 1 } }}>
                                                <InsightCard query={msg.content} onFollowUp={handleSuggestionClick} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            );
                        });
                    })()}
                    {queryLoading && <ThinkingSkeleton />}
                    <div ref={chatEndRef} />
                </Box>

                {/* Chat input */}
                <Box sx={{ flexShrink: 0 }}>
                    <ChatInputBar
                        ref={chatInputRef}
                        onSend={handleSend}
                        isLoading={queryLoading}
                        disabled={!selectedModelId}
                        placeholder={selectedModelId ? 'Ask anything about your data...' : 'Select a data model to start a new chat'}
                        dataModels={dataModels}
                        selectedModelId={selectedModelId}
                        onModelChange={(id) => { setSelectedModelId(id); fetchSuggestions(id); }}
                        suggestions={suggestions}
                        suggestionsLoading={suggestionsLoading}
                        onSuggestionClick={handleSuggestionClick}
                        onRefreshSuggestions={() => fetchSuggestions(selectedModelId)}
                        formatOptions={formatOptions}
                        onFormatChange={setFormatOptions}
                        aiModel={aiModel}
                        onAiModelChange={handleAiModelChange}
                    />
                </Box>
            </Box>

            {/* ── Drag divider ── */}
            <Box
                onMouseDown={onDividerMouseDown}
                sx={{
                    width: 8, flexShrink: 0, cursor: 'col-resize',
                    bgcolor: 'divider', position: 'relative',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color 0.15s',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.35)' },
                    '&:hover .divider-toggle': { opacity: 1 },
                }}
            >
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 2, height: 36, borderRadius: 2, bgcolor: 'rgba(99,102,241,0.4)', pointerEvents: 'none' }} />
                {!hasAIResponse && (
                    <Tooltip title={reportBuilderWidth === 0 ? 'Open Report Builder' : 'Collapse Report Builder'} placement="right">
                        <IconButton className="divider-toggle" size="small" onClick={(e) => { e.stopPropagation(); toggleReportBuilder(); }}
                            sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', width: 20, height: 20, bgcolor: 'background.paper', border: '1px solid', borderColor: 'rgba(99,102,241,0.4)', opacity: 0, transition: 'opacity 0.2s', zIndex: 10, p: 0, '&:hover': { bgcolor: 'rgba(99,102,241,0.15)', borderColor: '#6366f1' } }}>
                            {reportBuilderWidth === 0 ? <ChevronLeftIcon sx={{ fontSize: 12, color: '#6366f1' }} /> : <ChevronRightIcon sx={{ fontSize: 12, color: '#6366f1' }} />}
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* ── Report Builder panel ── */}
            <Box sx={{
                width: reportEditorOpen ? 'calc(100% - 380px)' : reportBuilderWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
                transition: isDraggingReportBuilder.current ? 'none' : 'width 0.4s cubic-bezier(0.22,1,0.36,1)',
                position: 'relative',
            }}>
                {reportBuilderWidth > 0 && (
                    <ReportBuilder
                        items={reportItems}
                        onRemove={handleRemoveReportItem}
                        onRenameTitle={handleRenameReportTitle}
                        onReorder={handleReorderReportItems}
                        onGenerateReport={handleOpenReportEditor}
                    />
                )}

                {/* ReportCanvas slides in over the top */}
                {reportEditorOpen && (
                    <ReportCanvas
                        items={reportEditorItems}
                        reportTitle={reportTitle}
                        sessionId={activeSession?.id}
                        token={token}
                        onBack={handleCloseReportEditor}
                        onOpenPreview={(items, title) => {
                            setPreviewItems(items);
                            setPreviewTitle(title);
                            setReportPreviewOpen(true);
                        }}
                    />
                )}
            </Box>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
            </Snackbar>

            <ReportPreviewDialog 
                open={reportPreviewOpen} 
                onClose={() => { setReportPreviewOpen(false); setPreviewItems(null); }} 
                reportItems={previewItems || reportItems} 
                reportTitle={previewTitle || reportTitle} 
            />
        </Box>
    );
};

export default AiInsightPage;

// ── Info Modal Component ──────────────────────────────────────────────────
const AiInsightInfoModal = ({ open, onClose }) => {
    const [tabIndex, setTabIndex] = React.useState(0);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: 640, borderRadius: 3, overflow: 'hidden' } }}>
            <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', pb: 0, pt: 2, px: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: 26 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Agent Insights Guide</Typography>
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><ClearIcon /></IconButton>
                </Box>
                <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} textColor="primary" indicatorColor="primary" sx={{ minHeight: 40, mt: 1 }}>
                    <Tab label="Getting Started" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.875rem' }} />
                    <Tab label="Chart Library" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.875rem' }} />
                    <Tab label="Pro Tips" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.875rem' }} />
                </Tabs>
            </DialogTitle>
            
            <DialogContent sx={{ p: 0, bgcolor: (t) => t.palette.mode === 'dark' ? '#0f1115' : '#f8fafc', overflowY: 'auto' }}>
                {/* Getting Started Tab */}
                {tabIndex === 0 && (
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary', fontSize: '1rem' }}>What is Agent Insights?</Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 2, fontSize: '0.9rem' }}>
                                Agent Insights is your personal data analyst. Unlike traditional dashboards where you have to click through filters, you can just ask natural language questions about your connected data models, and the AI will automatically write the SQL, fetch the data, generate narrative insights, and build the perfect visualizations for you.
                            </Typography>
                            <Alert severity="info" icon={<LightbulbIcon />} sx={{ borderRadius: 2, fontSize: '0.875rem' }}>
                                <strong>Context Aware:</strong> The AI remembers your previous questions in the same session. You can ask "What about last year?" and it will know what metric you are talking about.
                            </Alert>
                        </Box>

                        <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary', fontSize: '1rem' }}>The Report Builder Workflow</Typography>
                            <Box component="ol" sx={{ pl: 2.5, m: 0, color: 'text.secondary', '& li': { mb: 1.5, fontSize: '0.9rem', lineHeight: 1.7 } }}>
                                <li><strong>Query:</strong> Ask your question in the chat bar.</li>
                                <li><strong>Analyze:</strong> The AI decides whether your question needs a chart, a summary value, or just a table depending on how many dimensions and metrics are present.</li>
                                <li><strong>Pin:</strong> Found something interesting? Click the "Pin" icon on the top right of any result panel or chat message to save it to your Report Builder.</li>
                                <li><strong>Generate:</strong> Select your pinned items from the right sidebar and click "Generate Report". You can now re-order sections, edit text, and export the entire report as a polished PDF.</li>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Chart Library Tab */}
                {tabIndex === 1 && (
                    <Box sx={{ p: 3 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, fontSize: '0.9rem', lineHeight: 1.7 }}>
                            The AI engine automatically classifies your intent and selects the optimal chart type from our comprehensive reporting library:
                        </Typography>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                            
                            {/* Trends */}
                            <Box sx={{ bgcolor: 'background.paper', p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <ShowChartIcon sx={{ color: '#6366f1' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Trends & Time</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.6 }}>Best when the X-axis is time (Months, Years, Dates).</Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary', '& li': { mb: 0.75, fontSize: '0.85rem', lineHeight: 1.6 } }}>
                                    <li><strong>Line Chart:</strong> Standard time-series trends.</li>
                                    <li><strong>Area Chart:</strong> Emphasizes volume and cumulative totals over time.</li>
                                    <li><strong>Combo Chart:</strong> Line + Bar together (e.g. Revenue & Growth %).</li>
                                    <li><strong>Sparkline:</strong> Miniature trend line for quick glances.</li>
                                </Box>
                            </Box>

                            {/* Comparison */}
                            <Box sx={{ bgcolor: 'background.paper', p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <BarChartIcon sx={{ color: '#0ea5e9' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Comparison</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.6 }}>Comparing categories, rankings, and absolute values.</Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary', '& li': { mb: 0.75, fontSize: '0.85rem', lineHeight: 1.6 } }}>
                                    <li><strong>Bar Chart:</strong> Evaluating a single metric across groups.</li>
                                    <li><strong>Grouped Bar:</strong> Comparing 2-3 specific metrics side by side.</li>
                                    <li><strong>Stacked Bar:</strong> Seeing compositions across groups.</li>
                                    <li><strong>Waterfall Chart:</strong> Showing cumulative gains and losses (Revenue/Expense).</li>
                                </Box>
                            </Box>

                            {/* Composition */}
                            <Box sx={{ bgcolor: 'background.paper', p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <DonutLargeIcon sx={{ color: '#10b981' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Composition</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.6 }}>Parts of a whole and percentage breakdowns.</Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary', '& li': { mb: 0.75, fontSize: '0.85rem', lineHeight: 1.6 } }}>
                                    <li><strong>Donut & Pie Charts:</strong> Great for 3-5 major categories.</li>
                                    <li><strong>Treemap Chart:</strong> Best for hierarchical patterns or many small categories competing for share.</li>
                                </Box>
                            </Box>

                            {/* Flow & Performance */}
                            <Box sx={{ bgcolor: 'background.paper', p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <SpeedIcon sx={{ color: '#f59e0b' }} />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Flow & Performance</Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, lineHeight: 1.6 }}>Processes, KPIs, and multi-variable correlations.</Typography>
                                <Box component="ul" sx={{ pl: 2, m: 0, color: 'text.secondary', '& li': { mb: 0.75, fontSize: '0.85rem', lineHeight: 1.6 } }}>
                                    <li><strong>KPI Card / Gauge:</strong> Single powerful numbers or targets.</li>
                                    <li><strong>Funnel / Sankey:</strong> Conversion stages and complex routing mapping.</li>
                                    <li><strong>Heatmap:</strong> Matrices and geographic concentration.</li>
                                    <li><strong>Scatter / Bubble:</strong> Detecting outliers and multi-dimensional correlations.</li>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Pro Tips Tab */}
                {tabIndex === 2 && (
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ bgcolor: 'background.paper', p: 3, borderRadius: 2, border: '1px dashed', borderColor: 'primary.main' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.9rem' }}>
                                <AutoFixHighIcon fontSize="small" /> Prompt Engineering Tips
                            </Typography>
                            <Box component="ul" sx={{ pl: 2.5, m: 0, color: 'text.secondary', '& li': { mb: 1.25, fontSize: '0.9rem', lineHeight: 1.7 } }}>
                                <li><strong>Force a specific chart:</strong> If you don't like the AI's choice, just ask for it. <em>"Show me revenue by state as a heatmap."</em></li>
                                <li><strong>Define sorting natively:</strong> Make your output immediately useful. <em>"Show top 5 departments by volume."</em></li>
                                <li><strong>Leverage ambiguity chips:</strong> If the AI is unsure which "Revenue" you meant (Gross vs Net), it will provide yellow warning chips. Click the chip to instantly resolve the ambiguity.</li>
                                <li><strong>Use the Report Enhancer:</strong> In the Report Canvas, open the AI Assistant panel to have the LLM automatically re-write raw data points into executive paragraphs.</li>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>

    );
};

